import { useState, useCallback, useContext, useEffect } from 'react';
import { ModalDialog, ButtonBar, FlowForm, getKeyHandler } from 't-lib-ui/structure';
import { TextButton } from 't-lib-ui/widgets';
import { AppState } from '../context/general';
import { PAUSED_INDEFINITELY, PAUSE_DAYS_CHOICES, pauseEndSemi } from '../schema-copy/readmarker-helper';
import PauseIcon from './PauseIcon';
import './PauseFeedPopup.css';

/** "Thu, Oct 15" (with the year when it isn't this year) for a pause end semi. */
export function formatPauseEnd(pausedUntil: number, now = new Date()): string {
  const end = new Date(pausedUntil * 10000);
  return end.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    ...(end.getFullYear() != now.getFullYear() ? { year: 'numeric' } : {}),
  });
}

/** Re-renders the caller when a feed's pause changes (pausing notifies as an unread change). */
function usePauseState(feedId: string): number | undefined {
  const { feedEngine } = useContext(AppState);
  const [, setVersion] = useState(0);
  useEffect(() => feedEngine.onUnreadChange(id => id == feedId && setVersion(v => v + 1)), [feedEngine, feedId]);
  return feedEngine.getPausedUntil(feedId);
}

/**
 * What a paused feed shows about its pause: when it ends, and a button to resume. Text on the left,
 * the button on the right. Rendered below the FeedTile of a paused feed, and in the pause popup of a paused feed.
 */
export const PauseControls: React.FC<{ feedId: string; onDone?: () => void }> = ({ feedId, onDone }) => {
  const { feedEngine } = useContext(AppState);
  const pausedUntil = usePauseState(feedId);
  if (!pausedUntil) return null;
  const indefinite = pausedUntil >= PAUSED_INDEFINITELY;
  const resume = () => {
    feedEngine.unpauseFeed(feedId);
    onDone?.();
  };
  return (
    <div className="pauseControls">
      <p className="pauseControlsStatus">
        {indefinite ? <><strong>Paused</strong> until you resume it.</> : <><strong>Paused</strong> until <strong>{formatPauseEnd(pausedUntil)}</strong>.</>}
        {' '}New posts are marked as read as they arrive.
      </p>
      <div className="pauseControlsButtons">
        <TextButton type="major" size="small" label="Resume" onClick={resume} />
      </div>
    </div>
  );
};

export function usePauseFeed(): { openPauseFeed: (feedId: string) => void; pauseFeedPopup: React.ReactNode } {
  const [feedId, setFeedId] = useState<string | null>(null);
  const openPauseFeed = useCallback((id: string) => setFeedId(id), []);
  const pauseFeedPopup = <PauseFeedPopup feedId={feedId} onClose={() => setFeedId(null)} />;
  return { openPauseFeed, pauseFeedPopup };
}

const optionKeyHandler = getKeyHandler({ enterBehaviour: 'action', spaceBehaviour: 'action', onAction: (el: any) => el.click() });

/** Pauses a feed for 7 days, 30 days or indefinitely; for a paused feed, shows its PauseControls. */
const PauseFeedPopup: React.FC<{ feedId: string | null; onClose: () => void }> = ({ feedId, onClose }) => {
  const { feedEngine } = useContext(AppState);
  // Fixed while the dialog is open: pausing from here closes it, it doesn't turn into the controls.
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (feedId) setPaused(feedEngine.isFeedPaused(feedId));
  }, [feedId, feedEngine]);

  const pause = (pausedUntil: number) => {
    if (feedId) feedEngine.pauseFeed(feedId, pausedUntil);
    onClose();
  };
  const options = [
    ...PAUSE_DAYS_CHOICES.map(days => {
      const until = pauseEndSemi(days);
      return { label: `For ${days} days`, hint: `until ${formatPauseEnd(until)}`, until };
    }),
    { label: 'Indefinitely', hint: 'until you resume it', until: PAUSED_INDEFINITELY },
  ];

  return (
    <ModalDialog
      show={!!feedId}
      title={paused ? 'Feed paused' : 'Pause feed'}
      closeButton="close"
      onClose={onClose}
      className="pauseFeedPopup"
    >
      <FlowForm>
        {paused && feedId ? <PauseControls feedId={feedId} onDone={onClose} /> : <>
          <p className="pauseFeedNote">
            While a feed is paused, it won't show up as unread anymore. 
            The feed stays in your list, and you can still read it.
          </p>
          <div className="pauseFeedOptions">
            {options.map(o => (
              <button key={o.label} type="button" className="pauseFeedOption flowFocussable" tabIndex={0}
                onClick={() => pause(o.until)} onKeyDown={optionKeyHandler as any}>
                <PauseIcon size={18} circle className="pauseFeedOptionIcon" />
                <span className="pauseFeedOptionLabel">{o.label}</span>
                <span className="pauseFeedOptionHint">{o.hint}</span>
              </button>
            ))}
          </div>
          <ButtonBar className="full-margin-top" align="right">
            <TextButton type="minor" onClick={onClose} label="Cancel" />
          </ButtonBar>
        </>}
      </FlowForm>
    </ModalDialog>
  );
};

export default PauseFeedPopup;
